import { useId, useState, type ReactNode, type Ref } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { effectiveSecurityBlocks, type StackLike } from '@/lib/deployPlan';
import type { Catalog } from '@/types/catalog';

export type ReviewTarget = 'source' | 'target' | 'configure';

export interface ReviewStepProps {
  catalog: Pick<Catalog, 'name' | 'expose_port' | 'additional_ports'>;
  stack: StackLike & { name: string };
  endpointLabel: string;
  /** Muted note under the endpoint, e.g. "Auto-selected best fit". */
  endpointNote?: string | null;
  /** The app's domain; null hides the row (no domains known). */
  domainLabel?: string | null;
  /** `<subdomain>.<domain>` when a custom subdomain is set. */
  hostLabel?: string | null;
  externalUserId: string;
  displayName: string;
  appName: string;
  /** The effective instance name text (custom, app name, or generated preview). */
  instanceLabel: string;
  envVars: Readonly<Record<string, string>>;
  onEdit: (step: ReviewTarget) => void;
  headingRef?: Ref<HTMLHeadingElement>;
}

const MASK = '••••••••';
const FOCUS = 'outline-none focus-visible:ring-2 focus-visible:ring-ring';

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words text-sm">{children}</dd>
    </div>
  );
}

const Dash = () => <span className="text-muted-foreground">—</span>;

function Group({
  title,
  editStep,
  editLabel,
  onEdit,
  action,
  children,
}: {
  title: string;
  editStep?: ReviewTarget;
  editLabel?: string;
  onEdit: (step: ReviewTarget) => void;
  action?: ReactNode;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="space-y-3 rounded-lg border bg-card p-4 text-card-foreground">
      <div className="flex items-center justify-between gap-2">
        <h3 id={id} className="text-sm font-semibold">
          {title}
        </h3>
        <div className="flex items-center gap-2">
          {action}
          {editStep && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onEdit(editStep)}
              aria-label={editLabel ?? `Edit ${editStep}`}
              className={FOCUS}
            >
              Edit
            </Button>
          )}
        </div>
      </div>
      {children}
    </section>
  );
}

/** Use as the final wizard step: read-only summary of every choice before deploying. */
export function ReviewStep({
  catalog,
  stack,
  endpointLabel,
  endpointNote,
  domainLabel = null,
  hostLabel = null,
  externalUserId,
  displayName,
  appName,
  instanceLabel,
  envVars,
  onEdit,
  headingRef,
}: ReviewStepProps) {
  const [revealed, setRevealed] = useState(false);
  const envEntries = Object.entries(envVars).sort(([a], [b]) => a.localeCompare(b));
  const overlays = effectiveSecurityBlocks(stack).filter((b) => b.enabled);
  const exposed = (catalog.expose_port ?? 0) > 0;
  const extraPorts = catalog.additional_ports ?? [];

  return (
    <div className="space-y-4">
      <h2 ref={headingRef} tabIndex={-1} className="text-lg font-semibold outline-none">
        Review and deploy
      </h2>

      <Group title="Source" editStep="source" editLabel="Edit source" onEdit={onEdit}>
        <dl className="space-y-2">
          <Row label="Catalog">{catalog.name}</Row>
          <Row label="Stack">{stack.name}</Row>
          <Row label="Resources">
            <ul className="flex flex-wrap gap-1.5">
              {[`${stack.cpu_limit} ${stack.cpu_limit === 1 ? 'core' : 'cores'}`, `${stack.memory_mb} MB memory`, `${stack.storage_gb} GB storage`].map((t) => (
                <li key={t} className="rounded-md bg-muted px-2 py-0.5 text-xs tabular-nums">
                  {t}
                </li>
              ))}
            </ul>
            {stack.shared_cpu && (
              <p className="mt-1 text-xs text-muted-foreground">Shared CPU: not counted against endpoint CPU capacity.</p>
            )}
          </Row>
        </dl>
      </Group>

      <Group title="Target" editStep="target" editLabel="Edit target" onEdit={onEdit}>
        <dl className="space-y-2">
          <Row label="Endpoint">
            {endpointLabel}
            {endpointNote && <span className="block text-xs text-muted-foreground">{endpointNote}</span>}
          </Row>
          {domainLabel && <Row label="Domain">{domainLabel}</Row>}
          {hostLabel && <Row label="Hostname"><span className="font-mono">{hostLabel}</span></Row>}
        </dl>
      </Group>

      <Group title="Identity" editStep="configure" editLabel="Edit identity" onEdit={onEdit}>
        <dl className="space-y-2">
          <Row label="External user ID">
            <span className="font-mono text-xs">{externalUserId || <Dash />}</span>
          </Row>
          <Row label="Display name">{displayName.trim() || <Dash />}</Row>
          <Row label="App name">{appName || <Dash />}</Row>
          <Row label="Instance name">
            <span className="font-mono text-xs">{instanceLabel || <Dash />}</span>
          </Row>
        </dl>
      </Group>

      <Group
        title="Environment variables"
        editStep="configure"
        editLabel="Edit environment variables"
        onEdit={onEdit}
        action={
          envEntries.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={revealed}
              onClick={() => setRevealed((v) => !v)}
              className={FOCUS}
            >
              {revealed ? <EyeOff className="size-4" aria-hidden /> : <Eye className="size-4" aria-hidden />}
              Reveal values
            </Button>
          )
        }
      >
        {envEntries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No environment variables</p>
        ) : (
          <dl className="space-y-2">
            {envEntries.map(([k, v]) => (
              <Row key={k} label={k}>
                {revealed ? (
                  v === '' ? (
                    <span className="text-xs text-muted-foreground">(empty)</span>
                  ) : (
                    <span className="break-all font-mono text-xs">{v}</span>
                  )
                ) : (
                  <>
                    <span aria-hidden className="font-mono text-xs">
                      {MASK}
                    </span>
                    <span className="sr-only">Value hidden</span>
                  </>
                )}
              </Row>
            ))}
          </dl>
        )}
      </Group>

      <Group title="Routing" onEdit={onEdit}>
        <p className="text-xs text-muted-foreground">Change this in the Source step.</p>
        {exposed ? (
          <div className="space-y-1 text-sm">
            <p>A subdomain is assigned on deploy</p>
            {extraPorts.length > 0 && (
              <ul className="list-disc pl-4 text-xs text-muted-foreground">
                {extraPorts.map((p) => (
                  <li key={`${p.port}-${p.prefix}`}>
                    Additional subdomain prefix: <span className="font-mono text-foreground">{p.prefix}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No public routing</p>
        )}
      </Group>

      <Group title="Security overlays" onEdit={onEdit}>
        <p className="text-xs text-muted-foreground">Change this in the Source step.</p>
        {overlays.length === 0 ? (
          <p className="text-sm text-muted-foreground">No extra overlays. The platform safety floor always applies.</p>
        ) : (
          <ul className="space-y-2">
            {overlays.map((b) => (
              <li key={b.id} className="text-sm">
                <span className="font-medium">{b.name}</span>
                {b.description && <span className="block text-xs text-muted-foreground">{b.description}</span>}
              </li>
            ))}
          </ul>
        )}
      </Group>
    </div>
  );
}
